import { AlertTriangle, X } from 'lucide-react'
import type { ExtractedImagePreviewIssue } from '@/components/project/artifact-viewer/utils/extractedImagePreviewWarnings'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export interface ExtractedImageWarningBannerProps {
	issues: ExtractedImagePreviewIssue[]
	onDismiss: () => void
	width: number
	height: number
	formattedFileSize: string
	className?: string
}

export function ExtractedImageWarningBanner({
	issues,
	onDismiss,
	width,
	height,
	formattedFileSize,
	className,
}: ExtractedImageWarningBannerProps) {
	const { t } = useTranslation()

	if (issues.length === 0) {
		return null
	}

	const lines: { key: ExtractedImagePreviewIssue; text: string }[] = []
	for (const issue of issues) {
		switch (issue) {
			case 'large_dimensions':
				lines.push({
					key: issue,
					text: t('pages.projectDetail.artifactPreview.extractedImageWarning.largeDimensions', {
						width,
						height,
					}),
				})
				break
			case 'low_resolution':
				lines.push({
					key: issue,
					text: t('pages.projectDetail.artifactPreview.extractedImageWarning.lowResolution', {
						width,
						height,
					}),
				})
				break
			case 'large_file':
				lines.push({
					key: issue,
					text: t('pages.projectDetail.artifactPreview.extractedImageWarning.largeFile', {
						size: formattedFileSize,
					}),
				})
				break
			default: {
				const _exhaustive: never = issue
				throw new Error(`Unhandled extracted image issue: ${String(_exhaustive)}`)
			}
		}
	}

	return (
		<div
			className={cn(
				'shrink-0 rounded-lg border border-[#ff914d]/40 bg-[#ff914d]/12 text-foreground dark:border-[#ff914d]/45 dark:bg-[#ff914d]/14',
				className
			)}
			role="status"
			aria-live="polite"
		>
			<div className="flex w-full items-start gap-3 px-3 py-3">
				<AlertTriangle className="size-5 shrink-0 text-[#ff914d] dark:text-[#ff914d]" aria-hidden />
				<div className="min-w-0 flex-1 space-y-2 text-sm leading-snug">
					<p className="font-medium text-foreground">
						{t('pages.projectDetail.artifactPreview.extractedImageWarning.title')}
					</p>
					<ul className="list-disc space-y-1 pl-4 text-muted-foreground">
						{lines.map((line) => (
							<li key={line.key}>{line.text}</li>
						))}
					</ul>
				</div>
				<Button
					type="button"
					variant="ghost"
					size="icon-sm"
					className="shrink-0 text-foreground hover:bg-[#ff914d]/20"
					aria-label={t('pages.projectDetail.artifactPreview.extractedImageWarning.dismiss')}
					onClick={onDismiss}
				>
					<X className="size-4" aria-hidden />
				</Button>
			</div>
		</div>
	)
}
